<script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from "vue"
  import Swal from "sweetalert2"
  import api from "@/api/axios"

  const props = defineProps({
    // "board_key" en la BD: separa el tablero normal del de Corp para un
    // mismo usuario (antes era la clave de localStorage, se reutiliza tal cual)
    storageKey: {
      type: String,
      required: true
    },
    titulo: {
      type: String,
      default: "Cotizador Viaja VIP"
    },
    descripcion: {
      type: String,
      default:
        "Crea varias cotizaciones a la vez: cada una corre en su propia ventana " +
        "y puedes minimizarla mientras el proceso sigue en segundo plano."
    }
  })

  // =========================
  // PERSISTENCIA (backend — antes era localStorage, ver GET/PUT /board/:key
  // en boardRoutes.js: quedaba todo en el navegador y se perdía al borrar
  // cookies/datos del sitio o cambiar de equipo)
  // =========================

  const ventanas = ref([])
  const cargandoTablero = ref(true)
  let contadorId = 0
  // evita que el watcher de sincronización dispare peticiones mientras
  // todavía se está cargando el tablero inicial
  let cargado = false

  function conSesionInicial(v) {
    return {
      ...v,
      // snapshot inicial para que el modal restaure formulario y jobs
      sesionInicial: {
        datos: v.datos || null,
        job: v.job || null,
        estado: v.estado || "editando"
      }
    }
  }

  async function sincronizarAhora() {
    try {
      await api.put(`/board/${encodeURIComponent(props.storageKey)}`, {
        ventanas: ventanas.value.map(
          ({ id, numero, minimizada, estado, resumen, datos, job, pdfs, creadaEn }) =>
            ({ id, numero, minimizada, estado, resumen, datos, job, pdfs, creadaEn })
        )
      })
    } catch (err) {
      console.error("Error guardando el tablero:", err)
    }
  }

  let syncTimer = null
  function sincronizarConDebounce() {
    if (!cargado) return
    clearTimeout(syncTimer)
    syncTimer = setTimeout(sincronizarAhora, 600)
  }

  async function cargarVentanas() {
    try {
      const { data } = await api.get(`/board/${encodeURIComponent(props.storageKey)}`)
      let filas = data.ventanas || []
      let necesitaMigracion = false

      // Migración única desde la versión anterior (localStorage): si el
      // tablero en el servidor está vacío pero había datos guardados en
      // este navegador, se toman de ahí y se suben, para no perder lo que
      // el usuario tenía abierto justo al pasar a persistencia en BD.
      if (!filas.length) {
        const raw = localStorage.getItem(props.storageKey)
        if (raw) {
          try {
            const local = JSON.parse(raw)
            if (Array.isArray(local) && local.length) {
              filas = local
              necesitaMigracion = true
            }
          } catch { /* localStorage corrupto, se ignora */ }
        }
      }

      ventanas.value = filas.map(conSesionInicial)
      contadorId = ventanas.value.reduce((max, v) => Math.max(max, v.id), 0)

      if (necesitaMigracion) {
        await sincronizarAhora()
        localStorage.removeItem(props.storageKey)
      }
    } catch (err) {
      console.error("Error cargando el tablero:", err)
    } finally {
      cargado = true
      cargandoTablero.value = false
    }
  }

  // true si la fecha (ISO) no cae en el día de hoy (o no existe: ventanas
  // guardadas antes de este campo, que por definición son de días previos)
  function esDeOtroDia(fechaISO) {
    if (!fechaISO) return true
    return new Date(fechaISO).toDateString() !== new Date().toDateString()
  }

  // Igual que limpiarTablero(), pero automático y sin confirmación: solo se
  // llevan las cotizaciones COMPLETADAS de días anteriores a hoy — las de
  // hoy y las que sigan en edición/proceso se quedan intactas.
  function limpiarCompletadasAnteriores() {
    ventanas.value = ventanas.value.filter(v =>
      !(v.estado === "completada" && esDeOtroDia(v.creadaEn))
    )
  }

  // link de "ver" de Drive (webViewLink) -> link de descarga directa
  function getDriveDownloadUrl(url) {
    const match = url.match(/\/d\/([^/]+)/)
    if (!match) return url
    return `https://drive.google.com/uc?export=download&id=${match[1]}`
  }

  // Abre/descarga varias URLs con un solo clic. Todos los <a> se crean y se
  // hace click() en el mismo tick, sin ningún setTimeout de por medio: la
  // "activación" que da el navegador al clic del usuario solo cubre lo que
  // corre de forma síncrona dentro de ese mismo clic, así que cualquier
  // llamada diferida (aunque sea unos ms después) pierde esa activación y
  // el navegador la bloquea como si el script la hubiera disparado solo.
  //
  // target="_blank" es obligatorio SIEMPRE, incluso para descargar: sin él,
  // cada <a> navega la pestaña ACTUAL, y como el navegador solo permite una
  // navegación de nivel superior a la vez, el 2do click() cancela la
  // petición del 1ro antes de que llegue a descargarse (por eso solo bajaba
  // 1 de 2 archivos). Con target="_blank" cada click abre su propio
  // contexto nuevo, así que no se pisan entre sí — el navegador cierra ese
  // contexto solo apenas confirma que la respuesta es una descarga.
  function abrirVarios(urls, { descargar } = {}) {
    const anclas = urls.map((url) => {
      const a = document.createElement("a")
      a.href = descargar ? getDriveDownloadUrl(url) : url
      a.target = "_blank"
      a.rel = "noopener"
      a.style.display = "none"
      document.body.appendChild(a)
      return a
    })

    anclas.forEach(a => a.click())
    anclas.forEach(a => a.remove())
  }

  // Abre todos los PDF de la cotización con un solo clic.
  function verTodosLosPdf(pdfs) {
    abrirVarios(pdfs.map(p => p.pdfUrl))
  }

  // Descarga todos los PDF de la cotización con un solo clic.
  function descargarTodosLosPdf(pdfs) {
    abrirVarios(pdfs.map(p => p.pdfUrl), { descargar: true })
  }

  watch(ventanas, sincronizarConDebounce, { deep: true })

  const estadoMeta = {
    editando: {
      label: "En edición",
      clase: "bg-secondary",
      icono: "bi-pencil",
      spinner: false
    },
    buscando: {
      label: "Buscando vuelos",
      clase: "bg-primary",
      icono: "bi-search",
      spinner: true
    },
    cotizando: {
      label: "Generando cotización",
      clase: "bg-warning text-dark",
      icono: "bi-file-earmark-text",
      spinner: true
    },
    completada: {
      label: "Completada",
      clase: "bg-success",
      icono: "bi-check-circle",
      spinner: false
    },
    error: {
      label: "Error",
      clase: "bg-danger",
      icono: "bi-exclamation-triangle",
      spinner: false
    }
  }

  // título de la ventana: el teléfono ingresado, o el número como respaldo
  function tituloVentana(v) {
    const telefono = v.datos?.telefono?.trim()
    return telefono || `Cotización #${v.numero}`
  }

  // "yyyy-MM-dd" -> "dd/mm/yyyy" sin pasar por Date (evita corrimientos por huso horario)
  function formatoFecha(fecha) {
    const [anio, mes, dia] = (fecha || "").split("-")
    if (!anio || !mes || !dia) return null
    return `${dia}/${mes}/${anio}`
  }

  function fechasVentana(v) {
    const ida = formatoFecha(v.datos?.fechaIda)
    const regreso = formatoFecha(v.datos?.fechaRegreso)

    if (ida && regreso) return `${ida} → ${regreso}`
    return ida || regreso || null
  }

  // suma de adultos/niños/infantes de todas las habitaciones, discriminados
  function distribucionHabitaciones(v) {
    const habitaciones = v.datos?.habitaciones

    if (!Array.isArray(habitaciones) || !habitaciones.length) return null

    const totales = habitaciones.reduce((acc, h) => {
      acc.adultos += Number(h.adultos) || 0
      acc.ninos += Number(h.ninos) || 0
      acc.infantes += Number(h.infantes) || 0
      return acc
    }, { adultos: 0, ninos: 0, infantes: 0 })

    const partes = []

    if (totales.adultos) partes.push(`${totales.adultos} adulto${totales.adultos !== 1 ? "s" : ""}`)
    if (totales.ninos) partes.push(`${totales.ninos} niño${totales.ninos !== 1 ? "s" : ""}`)
    if (totales.infantes) partes.push(`${totales.infantes} infante${totales.infantes !== 1 ? "s" : ""}`)

    const numHab = habitaciones.length
    const etiquetaHab = `${numHab} habitación${numHab !== 1 ? "es" : ""}`

    return partes.length ? `${etiquetaHab}: ${partes.join(", ")}` : etiquetaHab
  }

  function nuevaVentana() {

    ventanas.value.forEach(v => {
      v.minimizada = true
    })

    contadorId += 1

    ventanas.value.push({
      id: contadorId,
      numero: contadorId,
      minimizada: false,
      estado: "editando",
      resumen: "",
      datos: null,
      job: null,
      creadaEn: new Date().toISOString(),
      sesionInicial: null
    })

  }

  function duplicarVentana(ventana) {

    ventanas.value.forEach(v => {
      v.minimizada = true
    })

    contadorId += 1

    // clon independiente: si se edita la copia no debe afectar al original
    const datosClonados = ventana.datos
      ? JSON.parse(JSON.stringify(ventana.datos))
      : null

    ventanas.value.push({
      id: contadorId,
      numero: contadorId,
      minimizada: false,
      estado: "editando",
      resumen: ventana.resumen,
      datos: datosClonados,
      job: null,
      creadaEn: new Date().toISOString(),
      // el modal restaura el formulario desde sesionInicial.datos al montarse
      // (mismo mecanismo que usa la persistencia en localStorage)
      sesionInicial: {
        datos: datosClonados,
        job: null,
        estado: "editando"
      }
    })

  }

  function minimizar(ventana) {
    ventana.minimizada = true
  }

  function restaurar(ventana) {

    ventanas.value.forEach(v => {
      v.minimizada = v.id !== ventana.id
    })

  }

  function toggleVentana(ventana) {

    if (ventana.minimizada) {
      restaurar(ventana)
    } else {
      minimizar(ventana)
    }

  }

  async function cerrarVentana(ventana) {

    const enProceso =
      ventana.estado === "buscando" ||
      ventana.estado === "cotizando"

    if (enProceso) {

      const r = await Swal.fire({
        icon: "warning",
        title: `${tituloVentana(ventana)} en proceso`,
        text: "Si la eliminas se perderá el progreso de esta cotización. ¿Deseas eliminarla?",
        showCancelButton: true,
        confirmButtonText: "Eliminar de todas formas",
        cancelButtonText: "Cancelar"
      })

      if (!r.isConfirmed) return

    }

    ventanas.value = ventanas.value.filter(v => v.id !== ventana.id)

  }

  const completadas = computed(() =>
    ventanas.value.filter(v => v.estado === "completada")
  )

  // solo las que ya tienen su(s) PDF listo(s) — recién completada, el
  // fetch a /quotes/by-code/:quoteCode puede tardar un instante
  const completadasConPdf = computed(() =>
    completadas.value.filter(v => v.pdfs?.length)
  )

  // Abre en pestañas nuevas los PDF de TODAS las cotizaciones completadas
  // del tablero (una cotización con 2 PDF, como Perú con/sin guía, cuenta
  // como 2). Se juntan todas las URLs primero para que abrirVarios() haga
  // un único click() por cada una dentro del mismo clic del usuario.
  function abrirCotizacionesCompletadas() {
    const urls = completadasConPdf.value.flatMap(v => v.pdfs.map(p => p.pdfUrl))
    abrirVarios(urls)
  }

  async function limpiarTablero() {

    if (!completadas.value.length) return

    const r = await Swal.fire({
      icon: "question",
      title: "Limpiar tablero",
      text: `Se eliminarán ${completadas.value.length} cotización(es) completada(s) del tablero. ¿Deseas continuar?`,
      showCancelButton: true,
      confirmButtonText: "Eliminar completadas",
      cancelButtonText: "Cancelar"
    })

    if (!r.isConfirmed) return

    const idsCompletadas = new Set(completadas.value.map(v => v.id))
    ventanas.value = ventanas.value.filter(v => !idsCompletadas.has(v.id))

  }

  // Limpieza automática diaria (sin confirmación, solo completadas de días
  // anteriores): se corre al entrar (después de cargar el tablero) y
  // también al volver a esta pestaña, por si quedó abierta de un día para
  // otro sin recargar.
  onMounted(async () => {
    await cargarVentanas()
    limpiarCompletadasAnteriores()
    document.addEventListener("visibilitychange", onVisibilityChange)
  })

  onUnmounted(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange)
  })

  function onVisibilityChange() {
    if (document.visibilityState === "visible") {
      limpiarCompletadasAnteriores()
    }
  }
</script>

<template>

  <div class="container py-4 cotizador-host">

    <!-- HEADER -->
    <div class="text-center mb-4">

      <h1 class="vip-title d-flex align-items-center justify-content-center gap-3">

        <span class="logo-vip">
          <i class="bi bi-airplane-fill plane-icon"></i>
        </span>

        <span class="title-animated">
          {{ titulo }}
        </span>

      </h1>

      <p class="text-muted mt-2 mb-0">
        {{ descripcion }}
      </p>

    </div>

    <!-- BOTON NUEVA -->
    <div class="d-flex align-items-center justify-content-center gap-3 flex-wrap mb-4">

      <button class="btn btn-primary btn-lg px-4" @click="nuevaVentana">
        <i class="bi bi-plus-lg me-2"></i>
        Nueva cotización
      </button>

      <button v-if="completadasConPdf.length" class="btn btn-outline-primary btn-lg px-4"
        @click="abrirCotizacionesCompletadas">
        <i class="bi bi-folder2-open me-2"></i>
        Abrir cotizaciones completadas
        <span class="badge bg-primary ms-1">{{ completadasConPdf.length }}</span>
      </button>

      <button v-if="completadas.length" class="btn btn-outline-secondary btn-lg px-4" @click="limpiarTablero">
        <i class="bi bi-eraser-fill me-2"></i>
        Limpiar tablero
        <span class="badge bg-secondary ms-1">{{ completadas.length }}</span>
      </button>

    </div>

    <!-- CARGANDO TABLERO -->
    <div v-if="cargandoTablero" class="text-center text-muted mt-5">
      <div class="spinner-border" role="status"></div>
      <p class="mt-3">Cargando tablero...</p>
    </div>

    <!-- CARDS RESUMEN (máx 3 por fila) -->
    <div v-else-if="ventanas.length" class="row g-3">

      <div v-for="v in ventanas" :key="v.id" class="col-12 col-sm-6 col-md-4">

        <div class="card card-cotizacion shadow-sm h-100" @click="restaurar(v)">

          <div class="card-body p-3">

            <div class="d-flex align-items-start justify-content-between mb-2">

              <h6 class="card-title d-flex align-items-center gap-2 mb-0">

                <i class="bi text-primary" :class="estadoMeta[v.estado].icono"></i>

                {{ tituloVentana(v) }}

              </h6>

              <div class="d-flex gap-1">

                <button type="button" class="btn btn-sm btn-outline-primary btn-duplicar-card"
                  title="Duplicar cotización" @click.stop="duplicarVentana(v)">
                  <i class="bi bi-files"></i>
                </button>

                <button type="button" class="btn btn-sm btn-outline-danger btn-eliminar-card" title="Eliminar cotización"
                  @click.stop="cerrarVentana(v)">
                  <i class="bi bi-trash"></i>
                </button>

              </div>

            </div>

            <p class="text-muted small mb-1">

              <i class="bi bi-geo-alt me-1"></i>

              {{ v.resumen || "Sin ruta definida" }}

            </p>

            <p v-if="fechasVentana(v)" class="text-muted small mb-1">

              <i class="bi bi-calendar-event me-1"></i>

              {{ fechasVentana(v) }}

            </p>

            <p v-if="distribucionHabitaciones(v)" class="text-muted small mb-3">

              <i class="bi bi-people-fill me-1"></i>

              {{ distribucionHabitaciones(v) }}

            </p>

            <div class="d-flex flex-column gap-2">

              <span class="badge d-inline-flex align-items-center gap-2 align-self-start" :class="estadoMeta[v.estado].clase">

                <span v-if="estadoMeta[v.estado].spinner" class="spinner-border spinner-border-sm"></span>

                {{ estadoMeta[v.estado].label }}

              </span>

              <!-- Perú genera 2 PDF (con guía / sin guía) bajo el mismo
                   quoteCode: ambos botones abren/descargan los 2 de una -->
              <div v-if="v.estado === 'completada' && v.pdfs?.length" class="d-flex gap-2">

                <button type="button" class="btn btn-sm btn-primary" @click.stop="verTodosLosPdf(v.pdfs)">
                  <i class="bi bi-file-earmark-pdf me-1"></i>
                  {{ v.pdfs.length > 1 ? "Ver ambos" : "Ver PDF" }}
                </button>

                <button type="button" class="btn btn-sm btn-dark" @click.stop="descargarTodosLosPdf(v.pdfs)">
                  <i class="bi bi-download me-1"></i>
                  {{ v.pdfs.length > 1 ? "Descargar ambos" : "Descargar PDF" }}
                </button>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

    <div v-else class="text-center text-muted mt-5">

      <i class="bi bi-window-plus" style="font-size: 3rem;"></i>

      <p class="mt-3">
        Aún no tienes cotizaciones abiertas.
        Haz clic en <strong>Nueva cotización</strong> para empezar.
      </p>

    </div>

  </div>

  <!-- VENTANAS (siempre montadas: v-show mantiene los procesos vivos) -->
  <div v-for="v in ventanas" :key="v.id" v-show="!v.minimizada" class="modal fade show ventana-cotizacion"
    @click.self="minimizar(v)">

    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">

      <div class="modal-content">

        <div class="modal-header py-2">

          <h5 class="modal-title d-flex align-items-center flex-wrap gap-2 mb-0">

            <i class="bi bi-airplane-fill text-primary"></i>

            {{ tituloVentana(v) }}

            <small v-if="v.resumen" class="text-muted fw-normal">
              {{ v.resumen }}
            </small>

            <span class="badge d-flex align-items-center gap-2" :class="estadoMeta[v.estado].clase">

              <span v-if="estadoMeta[v.estado].spinner" class="spinner-border spinner-border-sm"></span>

              {{ estadoMeta[v.estado].label }}

            </span>

          </h5>

          <div class="d-flex align-items-center gap-2">

            <button type="button" class="btn btn-sm btn-outline-secondary" title="Minimizar" @click="minimizar(v)">
              <i class="bi bi-dash-lg"></i>
            </button>

            <button type="button" class="btn-close" title="Eliminar" @click="cerrarVentana(v)"></button>

          </div>

        </div>

        <div class="modal-body">

          <!-- el formulario concreto (normal o corp) lo aporta la vista padre -->
          <slot :ventana="v" />

        </div>

      </div>

    </div>

  </div>

  <!-- BARRA DE TAREAS -->
  <div v-if="ventanas.length" class="taskbar-cotizaciones shadow-lg">

    <div class="d-flex align-items-center gap-2 flex-wrap">

      <span class="text-muted small me-2 d-none d-md-inline">
        <i class="bi bi-stack me-1"></i>
        Cotizaciones:
      </span>

      <div v-for="v in ventanas" :key="v.id" class="taskbar-chip" :class="{ activa: !v.minimizada }"
        @click="toggleVentana(v)">

        <span v-if="estadoMeta[v.estado].spinner" class="spinner-border spinner-border-sm text-primary"></span>

        <i v-else class="bi" :class="[estadoMeta[v.estado].icono, {
          'text-success': v.estado === 'completada',
          'text-danger': v.estado === 'error'
        }]"></i>

        <span class="fw-semibold">
          {{ tituloVentana(v) }}
        </span>

        <span v-if="v.resumen" class="text-muted small d-none d-sm-inline">
          {{ v.resumen }}
        </span>

        <span class="badge" :class="estadoMeta[v.estado].clase">
          {{ estadoMeta[v.estado].label }}
        </span>

        <button type="button" class="btn-close btn-close-chip" title="Eliminar" @click.stop="cerrarVentana(v)"></button>

      </div>

      <button class="btn btn-sm btn-outline-primary ms-auto" @click="nuevaVentana">
        <i class="bi bi-plus-lg"></i>
        Nueva
      </button>

    </div>

  </div>

</template>

<style>
  .cotizador-host {
    background-color: #f8f9fa;
    border-radius: 6px;
    margin-bottom: 24px;
  }

  /* con barra de tareas visible, el footer sube para no quedar tapado */
  body:has(.taskbar-cotizaciones) .footer {
    margin-bottom: 72px;
  }

  /* ===== CARDS RESUMEN ===== */
  .card-cotizacion {
    cursor: pointer;
    border: 1px solid #dee2e6;
    transition: all .15s ease;
  }

  .card-cotizacion:hover {
    border-color: #0d6efd;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(13, 110, 253, 0.15) !important;
  }

  .btn-eliminar-card,
  .btn-duplicar-card {
    padding: 2px 8px;
    font-size: .75rem;
    line-height: 1.2;
  }

  /* ===== VENTANAS ===== */
  /* display block SIN !important (a diferencia de d-block de Bootstrap)
     para que el display:none inline de v-show pueda ocultar la ventana */
  .modal.ventana-cotizacion {
    display: block;
    background: rgba(0, 0, 0, .5);
    /* deja visible la barra de tareas */
    bottom: 64px;
  }

  .ventana-cotizacion .modal-dialog-scrollable .modal-body {
    background-color: #f8f9fa;
  }

  /* ===== BARRA DE TAREAS ===== */
  /* z-index por debajo del sidenav (999) para no taparlo en modo overlay */
  .taskbar-cotizaciones {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 998;

    background: #fff;
    border-top: 1px solid #dee2e6;

    padding: 10px 16px;
    min-height: 56px;
  }

  /* En pantallas xl+ el sidenav está fijo a la izquierda: la barra arranca
     donde arranca .main-content (mismo offset que aplica Argon) */
  @media (min-width: 1200px) {
    body:has(.sidenav.fixed-start) .taskbar-cotizaciones {
      left: 17.125rem;
    }
  }

  .taskbar-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    padding: 6px 12px;

    border: 1px solid #dee2e6;
    border-radius: 999px;

    background: #f8f9fa;

    cursor: pointer;

    transition: all .15s ease;

    user-select: none;
  }

  .taskbar-chip:hover {
    background: #e9ecef;
  }

  .taskbar-chip.activa {
    border-color: #0d6efd;
    background: #e7f1ff;
  }

  .btn-close-chip {
    font-size: .6rem;
    margin-left: 2px;
  }

  /* ===== HEADER VIP ===== */
  .vip-title {
    font-weight: 800;
  }

  .logo-vip {
    width: 58px;
    height: 58px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 18px;

    background: linear-gradient(135deg,
        #0d6efd,
        #6ea8fe);

    box-shadow:
      0 10px 30px rgba(13, 110, 253, 0.25);

    position: relative;
    overflow: hidden;
  }

  /* BRILLO */
  .logo-vip::before {
    content: "";

    position: absolute;

    width: 120%;
    height: 120%;

    background: linear-gradient(120deg,
        transparent,
        rgba(255, 255, 255, 0.35),
        transparent);

    transform: rotate(25deg);

    animation: shine 3s infinite;
  }

  /* AVION */
  .plane-icon {
    color: white;

    font-size: 1.4rem;

    transform: rotate(-25deg);

    animation: floatPlane 2.8s ease-in-out infinite;

    z-index: 2;
  }

  /* TEXTO PREMIUM */
  .title-animated {
    background: linear-gradient(90deg,
        #0d6efd,
        #6ea8fe,
        #0d6efd);

    background-size: 200% auto;

    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    animation: gradientMove 4s linear infinite;

    letter-spacing: -0.5px;
  }

  /* ANIMACION AVION */
  @keyframes floatPlane {

    0% {
      transform:
        rotate(-25deg) translateY(0px) translateX(0px);
    }

    50% {
      transform:
        rotate(-20deg) translateY(-4px) translateX(3px);
    }

    100% {
      transform:
        rotate(-25deg) translateY(0px) translateX(0px);
    }
  }

  /* ANIMACION BRILLO */
  @keyframes shine {

    0% {
      left: -150%;
    }

    100% {
      left: 150%;
    }
  }

  /* ANIMACION TEXTO */
  @keyframes gradientMove {

    0% {
      background-position: 0% center;
    }

    100% {
      background-position: 200% center;
    }
  }
</style>
